import React, { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@material-ui/core/Box'
import Avatar from '@material-ui/core/Avatar'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import ChevronLeftIcon from 'components/material-symbol/icons/ChevronLeft'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import CheckIcon from 'components/material-symbol/icons/Check'
import { getCoachAvatarFontSize } from 'components/coach-user-avatar'
import { getInitials } from 'lib/text-display'
import { getStudentPickerLabel } from './utils'

const SCROLL_STEP = 220

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(1.5),
    },
  },
  label: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    lineHeight: 1.3,
    textTransform: 'uppercase',
    color: '#6B7C87',
    flexShrink: 0,
  },
  chipTrack: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
    [theme.breakpoints.down('xs')]: {
      flex: '1 1 100%',
    },
  },
  chipScroller: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    overflowX: 'auto',
    flexWrap: 'nowrap',
    flex: '1 1 auto',
    minWidth: 0,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  scrollButton: {
    flexShrink: 0,
    width: 32,
    height: 32,
    border: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
    color: '#00205B',
    '&:hover': {
      backgroundColor: '#F4F7F9',
    },
    '&.Mui-disabled': {
      color: '#B0BEC5',
      backgroundColor: '#FFFFFF',
    },
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    height: 32,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 999,
    border: '1px solid #70787C',
    backgroundColor: '#FFFFFF',
    color: '#40484C',
    cursor: 'pointer',
    font: 'inherit',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    transition:
      'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: '#F4F7F9',
    },
  },
  chipSelected: {
    backgroundColor: '#EBF0F2',
    borderColor: '#EBF0F2',
    color: '#172024',
    '&:hover': {
      borderColor: '#EBF0F2',
    },
  },
  avatar: {
    width: 24,
    height: 24,
    fontSize: getCoachAvatarFontSize(24),
    fontWeight: 500,
    color: '#00205B',
    backgroundColor: '#E8F1FF',
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  avatarSelected: {
    color: '#FFFFFF',
    backgroundColor: '#001C57',
  },
  chipLabel: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.2,
  },
  chipCheck: {
    display: 'flex',
    alignItems: 'center',
    color: '#001C57',
    flexShrink: 0,
    '& svg': {
      fontSize: 16,
    },
  },
}))

const StudentChip = ({ student, selected, onSelect, classes }) => {
  const label = getStudentPickerLabel(student)
  const initials = getInitials(`${student.firstName} ${student.lastName}`)

  return (
    <Box
      component="button"
      type="button"
      className={`${classes.chip} ${selected ? classes.chipSelected : ''}`}
      onClick={() => onSelect(student.id)}
      aria-pressed={selected}
    >
      <Avatar
        className={`${classes.avatar} ${
          selected ? classes.avatarSelected : ''
        }`}
        aria-hidden
      >
        {initials}
      </Avatar>
      <Typography component="span" className={classes.chipLabel}>
        {label}
      </Typography>
      {selected ? (
        <Box className={classes.chipCheck} aria-hidden>
          <CheckIcon fontSize="small" />
        </Box>
      ) : null}
    </Box>
  )
}

const ViewingGolfForSelector = ({
  students,
  selectedStudent,
  setSelectedStudent,
}) => {
  const classes = useStyles()
  const scrollRef = useRef(null)
  const [showArrows, setShowArrows] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current
    if (!element) return

    const hasOverflow = element.scrollWidth > element.clientWidth + 1
    setShowArrows(hasOverflow)
    setCanScrollLeft(element.scrollLeft > 0)
    setCanScrollRight(
      element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
    )
  }, [])

  useEffect(() => {
    updateScrollState()
    const element = scrollRef.current
    if (!element) return undefined

    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(element)
    element.addEventListener('scroll', updateScrollState, { passive: true })

    return () => {
      resizeObserver.disconnect()
      element.removeEventListener('scroll', updateScrollState)
    }
  }, [students, updateScrollState])

  const handleScroll = direction => {
    const element = scrollRef.current
    if (!element) return

    element.scrollBy({
      left: direction === 'left' ? -SCROLL_STEP : SCROLL_STEP,
      behavior: 'smooth',
    })
  }

  if (students.length <= 1) {
    return null
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.inner}>
        <Typography component="span" className={classes.label}>
          Viewing golf for
        </Typography>

        <Box className={classes.chipTrack}>
          {showArrows ? (
            <IconButton
              className={classes.scrollButton}
              size="small"
              onClick={() => handleScroll('left')}
              disabled={!canScrollLeft}
              aria-label="Scroll students left"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          ) : null}

          <Box ref={scrollRef} className={classes.chipScroller}>
            {students.map(student => (
              <StudentChip
                key={student.id}
                student={student}
                selected={student.id === selectedStudent}
                onSelect={setSelectedStudent}
                classes={classes}
              />
            ))}
          </Box>

          {showArrows ? (
            <IconButton
              className={classes.scrollButton}
              size="small"
              onClick={() => handleScroll('right')}
              disabled={!canScrollRight}
              aria-label="Scroll students right"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
      </Box>
    </Box>
  )
}

export default ViewingGolfForSelector
